import { formatShortCivilDate, parseCivilDate, shiftCivilDays } from '../../domain/finance';

const monthOrdinal = (date) => {
  const civil = parseCivilDate(date);
  return civil ? civil.year * 12 + civil.month - 1 : null;
};

// One slot per existing point: this helper never groups, drops or changes data.
// Route/context is deliberately not an input. Dates set granularity; available
// width only controls how many of those labels can be displayed without overlap.
export const buildTemporalAxisLabels = (data, containerWidth = 720) => {
  if (!data.length) return [];
  const startDate = data[0].startDate;
  const endDate = data.at(-1).endDate;
  const validDates = data.every((item) => parseCivilDate(item.startDate)
    && parseCivilDate(item.endDate) && item.startDate <= item.endDate);
  const dated = validDates && startDate <= endDate;
  const shortRange = dated && endDate <= shiftCivilDays(startDate, 61);
  const span = dated ? monthOrdinal(endDate) - monthOrdinal(startDate) + 1 : 0;
  const cadence = !dated || shortRange ? 0
    : span <= 6 ? 1 : span <= 12 ? 2 : span <= 18 ? 3 : span <= 36 ? 6 : 12;
  const yearsOnly = dated && !shortRange && span > 36;
  const minSpacing = yearsOnly ? 58 : 72;
  const width = Number.isFinite(containerWidth) && containerWidth > 0 ? containerWidth : 720;
  const plotWidth = Math.max(1, width * (692 / 720));
  const dateFor = (index) => index === data.length - 1 && data.length > 1
    ? data[index].endDate : data[index].startDate;
  const labelFor = (index) => {
    if (!dated) return data[index].label;
    const date = dateFor(index);
    if (shortRange) return formatShortCivilDate(date);
    if (yearsOnly) return date.slice(0, 4);
    return formatShortCivilDate(date).replace(/^\d+\s+/, '') + '/' + date.slice(2, 4);
  };
  const x = (index) => data.length <= 1 ? 0 : index * plotWidth / (data.length - 1);
  const selected = [0];
  for (let index = 1; index < data.length - 1; index += 1) {
    const previous = selected.at(-1);
    const cadenceReached = cadence === 0
      || monthOrdinal(dateFor(index)) - monthOrdinal(dateFor(previous)) >= cadence;
    if (cadenceReached && x(index) - x(previous) >= minSpacing
      && labelFor(index) !== labelFor(previous)) selected.push(index);
  }
  const end = data.length - 1;
  if (end > 0 && labelFor(end) !== labelFor(selected.at(-1))) {
    while (selected.length > 1 && x(end) - x(selected.at(-1)) < minSpacing) selected.pop();
    if (x(end) - x(selected.at(-1)) >= minSpacing) selected.push(end);
  }
  const visible = new Set(selected);
  return data.map((item, index) => visible.has(index) ? labelFor(index) : null);
};
